import React, { useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  UsersIcon,
  BeakerIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  WrenchScrewdriverIcon,
  TicketIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../utils/helpers';
import { useAuthStore, useSysadminStore } from '../../stores';
import { CompanySelector } from './CompanySelector';
import type { Permission } from '../../types';

interface NavItem {
  name: string;
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  permissions?: Permission[]; // At least one of these permissions is required
}

const navigation: NavItem[] = [
  { name: 'Dashboard', path: '/dashboard', icon: HomeIcon, permissions: ['dashboard:view'] },
  { name: 'Insights', path: '/insights', icon: ChartBarIcon, permissions: ['insights:view'] },
  { name: 'Orders', path: '/orders', icon: ClipboardDocumentListIcon, permissions: ['orders:view'] },
  { name: 'Patients', path: '/patients', icon: UserGroupIcon, permissions: ['patients:view'] },
  { name: 'Users', path: '/users', icon: UsersIcon, permissions: ['users:view'] },
  { name: 'Products', path: '/medications', icon: BeakerIcon, permissions: ['offerings:view'] },
  { name: 'Coupons', path: '/coupons', icon: TicketIcon, permissions: ['coupons:view'] },
  { name: 'Email Templates', path: '/email-templates', icon: EnvelopeIcon, permissions: ['email_templates:view'] },
  { name: 'Configurations', path: '/configurations', icon: WrenchScrewdriverIcon, permissions: ['configurations:view'] },
  { name: 'Settings', path: '/settings', icon: Cog6ToothIcon }, // Settings is available to all authenticated users
];

const sysadminNavigation: NavItem[] = [
  { name: 'Users & Roles', path: '/admin/users-roles', icon: ShieldCheckIcon },
  { name: 'Email Templates', path: '/admin/email-templates', icon: EnvelopeIcon },
  { name: 'Questionnaires', path: '/admin/questionnaires', icon: ClipboardDocumentCheckIcon },
  { name: 'Medications', path: '/admin/medications', icon: BeakerIcon },
  { name: 'Pharmacies', path: '/admin/pharmacies', icon: BuildingStorefrontIcon },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { logout, business, permissions, user } = useAuthStore();
  const { mode } = useSysadminStore();

  const isSysadmin = user?.isSuperAdmin === true;

  // Filter navigation items based on user permissions
  const filteredNavigation = useMemo(() => {
    // Sysadmin in system mode: show sysadmin nav
    if (isSysadmin && mode === 'system') {
      return sysadminNavigation;
    }

    // Sysadmin in company mode: show all company nav (sysadmin has all permissions)
    if (isSysadmin && mode === 'company') {
      return navigation;
    }

    // Regular user: filter by permissions
    return navigation.filter((item) => {
      if (!item.permissions || item.permissions.length === 0) {
        return true;
      }
      return item.permissions.some(p => permissions.includes(p));
    });
  }, [permissions, isSysadmin, mode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white dark:bg-secondary-900 border-r border-secondary-200 dark:border-secondary-700 transform transition-transform duration-300 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          {isSysadmin ? (
            <div className="flex flex-col px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">CH</span>
                </div>
                <h1 className="font-display font-semibold text-secondary-900 dark:text-secondary-50">Capture Health</h1>
              </div>
              <CompanySelector />
            </div>
          ) : (
            <div className="flex items-center h-16 px-6 border-b border-secondary-200 dark:border-secondary-700">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">CH</span>
                </div>
                <div>
                  <h1 className="font-display font-semibold text-secondary-900 dark:text-secondary-50">Capture Health</h1>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate max-w-[140px]">
                    {business?.name || 'Client Portal'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin">
            {filteredNavigation.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => onClose()}
                className={({ isActive }) =>
                  cn(
                    'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                      : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900 dark:text-secondary-400 dark:hover:bg-secondary-800 dark:hover:text-secondary-100'
                  )
                }
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Documentation */}
          <div className="px-3 pb-2">
            <NavLink
              to="/documentation"
              onClick={() => onClose()}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900 dark:text-secondary-400 dark:hover:bg-secondary-800 dark:hover:text-secondary-100'
                )
              }
            >
              <BookOpenIcon className="w-5 h-5 mr-3" />
              Documentation
            </NavLink>
          </div>

          {/* Logout */}
          <div className="p-3 border-t border-secondary-200 dark:border-secondary-700">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2.5 rounded-lg text-sm font-medium text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900 dark:text-secondary-400 dark:hover:bg-secondary-800 dark:hover:text-secondary-100 transition-all duration-200"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

